'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import AboutSection from '@/components/AboutSection';
import Footer from '@/components/Footer';
import AirportSearchInput from '@/components/AirportSearchInput';
import BlogArticlesPreview from '@/components/BlogArticlesPreview';
import FaqAccordion from '@/components/FaqAccordion';
import { Plane, MapPin, Star, Shield, Clock, ArrowRight, Globe, Phone, X, CheckCircle, Building2, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';

const HOME_INTRO_PARAGRAPHS = [
  'Who says traveling can burn a hole in your pocket? FlightAirHub is an expert online travel agency offering easy and affordable travel solutions. Get amazing deals on flight booking and explore your dream destinations. Whether you are traveling for work, leisure, or due to an emergency, you will surely find great deals on our site.',
  'If you are a travel enthusiast who cannot control their wanderlust but wants to travel within a budget, book from FlightAirHub. We are your go-to travel buddy, ready to plan and book your vacation package.',
  'Hotel Packages, Vacation Packages, Rental Car Packages',
  'New to the online flight booking process? Don’t worry! We make online travel booking easy and quick for you. Booking a flight on our portal is just a few clicks away. No need to juggle between multiple sites to compare prices, as you can do it all here.',
  'Not sure how to book yourself? You can call us and connect with our representative. Our expert travel agents will guide you through every step of the flight booking procedure. Solo, couple, group, business, or flights for minors- we deal in all sorts of flight bookings.',
  'FlightAirHub aims to always serve customers with the best deals on flight bookings. That’s why we assure competitive pricing on flight bookings and travel packages.',
  'Get the best competitive prices on flight bookings at no hidden charges. You even get our price match guarantee. So, if you are getting a lower fare on another online travel agency’s portal, let us know. Our travel agent will match that airfare for you.',
  'Even if you find a lower fare for the same booking you made with us, you can bring that to our notice. Under our price match guarantee promise, we will pay you the difference to match the price.',
  'What more do you get when booking a flight with FlightAirHub? Booking a flight and travel package on FlightAirHub is very easy. Simply visit our website, select your travel departure & arrival destinations, and type your preferred travel dates. A list of scheduled flights on that day will appear on your screen. Select the one that fits your plan and budget, and you are good to go.',
  'So what are you waiting for? Book a flight today on FlightAirHub and enjoy an effortless travel experience.',
];

const FEATURES = [
  {
    icon: Shield,
    title: 'Competitive Pricing',
    desc: 'Our competitive prices give you the best deals and great discounts on flight tickets.'
  },
  {
    icon: Clock,
    title: '24/7 Customer Support',
    desc: 'We offer 24/7 customer support to help you with your issues.'
  },
  {
    icon: Globe,
    title: 'Stress-Free Booking',
    desc: 'With our effortless, stress-free booking process, we make travel quick and easy.'
  },
  {
    icon: Star,
    title: 'Quick Assistance',
    desc: 'Get quick and reliable assistance from our experts if you have any queries.'
  },
];

export default function HomeClient() {
  const router = useRouter();
  const [tripType, setTripType] = useState('Round Trip');
  const [homeLocations, setHomeLocations] = useState([]);
  const [latestBlog, setLatestBlog] = useState(null);
  const [latestArticle, setLatestArticle] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [introExpanded, setIntroExpanded] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({
    from: '',
    to: '',
    departure: '',
    returnDate: '',
    passengers: '1 Adult',
    cabinClass: 'Economy',
  });

  function handleField(k, v) { setForm(f => ({ ...f, [k]: v })); if (validationError) setValidationError(''); }

  useEffect(() => {
    fetch('/api/locations').then(r => r.json()).then(d => setHomeLocations(d.slice(0, 6))).catch(() => {});
    fetch('/api/latest-posts').then(r => r.json()).then(d => { setLatestBlog(d.latestBlog); setLatestArticle(d.latestArticle); }).catch(() => {});
    fetch('/api/faqs').then(r => r.json()).then(d => setFaqs(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  function handleSearch() {
    if (!form.from.trim()) { setValidationError('Please enter your departure city or airport.'); return; }
    if (!form.to.trim()) { setValidationError('Please enter your destination city or airport.'); return; }
    if (!form.departure) { setValidationError('Please select a departure date.'); return; }
    if (tripType === 'Round Trip' && !form.returnDate) { setValidationError('Please select a return date for your round trip.'); return; }
    setValidationError('');
    const q = new URLSearchParams({
      from: form.from,
      to: form.to,
      departure: form.departure,
      return: form.returnDate,
      passengers: form.passengers,
      class: form.cabinClass,
      trip: tripType,
    });
    router.push(`/flights?${q.toString()}`);
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative flex items-center justify-center bg-slate-900">
        <img
          src="/images/hero-flight-sky.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-linear-to-b from-slate-900/60 via-slate-900/30 to-slate-900" />
        <div className="absolute top-32 left-12 opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
          <Plane size={28} className="text-white -rotate-12" />
        </div>
        <div className="absolute top-48 right-20 opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
          <Plane size={18} className="text-sky-400 rotate-12" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto py-10 lg:py-16">
          <div className="inline-flex items-center gap-2 bg-sky-500/20 border border-sky-500/30 text-sky-300 text-sm px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 bg-sky-400 rounded-full animate-pulse" />
            Let the Journey Begin with FlightAirHub
          </div>
          <h1 className="font-display text-5xl md:text-5xl font-bold text-white mb-6 leading-none">
            Uncover Exciting Offers and <span className="text-sky-400">Up to 15% Off on First-Time Bookings</span>
          </h1>
          <p className="text-slate-300 text-lg md:text-xl mx-auto mb-12 leading-relaxed">
            Unlock hard-to-miss deals, grab unbelievable discounts, and book flights in just a few clicks.
          </p>

          {/* Search Box */}
          <div id="search" className="bg-white rounded-2xl p-4 md:p-6 shadow-2xl max-w-4xl mx-auto">
            <div className="flex gap-4 mb-5 border-b border-slate-100 pb-4">
              {['One Way', 'Round Trip'].map((t) => (
                <button key={t} onClick={() => setTripType(t)} className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${tripType === t ? 'bg-sky-500 text-white' : 'text-slate-500 hover:text-slate-800'}`}>
                  {t}
                </button>
              ))}
            </div>

            {/* Validation error popup */}
            {validationError && (
              <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <AlertCircle size={18} className="flex-shrink-0 text-red-500" />
                <span className="flex-1">{validationError}</span>
                <button onClick={() => setValidationError('')} aria-label="Dismiss error" className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                  <X size={16} />
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <AirportSearchInput
                label="From"
                value={form.from}
                onChange={v => handleField('from', v)}
                placeholder="Atlanta (ATL)"
              />
              <AirportSearchInput
                label="To"
                value={form.to}
                onChange={v => handleField('to', v)}
                placeholder="Where are you going?"
              />
              <div>
                <label htmlFor="search-departure" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Departure</label>
                <input id="search-departure" type="date" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.departure} onChange={e => handleField('departure', e.target.value)} />
              </div>
              <div>
                <label htmlFor="search-return" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Return</label>
                <input id="search-return" type="date" className={`w-full px-3 py-3 rounded-xl border text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm transition-colors ${tripType === 'One Way' ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' : 'border-slate-200'}`}
                  value={form.returnDate} onChange={e => handleField('returnDate', e.target.value)} disabled={tripType === 'One Way'} />
              </div>
            </div>
            <div className="flex gap-3 mt-4 items-end">
              <div className="flex-1">
                <label htmlFor="search-passengers" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Passengers</label>
                <select id="search-passengers" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.passengers} onChange={e => handleField('passengers', e.target.value)}>
                  <option>1 Adult</option><option>2 Adults</option><option>2 Adults, 1 Child</option><option>2 Adults, 2 Children</option>
                </select>
              </div>
              <div className="flex-1">
                <label htmlFor="search-cabin-class" className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1 text-start">Class</label>
                <select id="search-cabin-class" className="w-full px-3 py-3 rounded-xl border border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 text-sm" value={form.cabinClass} onChange={e => handleField('cabinClass', e.target.value)}>
                  <option>Economy</option><option>Premium Economy</option><option>Business</option><option>First Class</option>
                </select>
              </div>
              <button onClick={handleSearch} className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors flex items-center gap-2 whitespace-nowrap">
                <Plane size={18} /> Search Flights
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-sky-500 text-white py-6">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[['500+', 'Airlines'], ['120+', 'Countries'], ['100K+', 'Happy travelers'], ['$50', 'Average savings']].map(([n, l]) => (
            <div key={l}><div className="text-3xl font-display font-bold">{n}</div><div className="text-sky-100 text-sm mt-1">{l}</div></div>
          ))}
        </div>
      </section>

      {/* Intro content */}
      <section className="py-14 px-6 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            FlightAirHub: Your Travel Journey Begins Here!
          </h2>
          <div className="text-slate-600 text-base md:text-lg leading-relaxed space-y-4">
            <p>{HOME_INTRO_PARAGRAPHS[0]}</p>
            {introExpanded && HOME_INTRO_PARAGRAPHS.slice(1).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {HOME_INTRO_PARAGRAPHS.length > 1 && (
            <button
              type="button"
              onClick={() => setIntroExpanded(v => !v)}
              aria-expanded={introExpanded}
              className="mt-3 inline-flex items-center gap-1.5 text-sky-600 hover:text-sky-700 font-semibold text-sm transition-colors"
            >
              {introExpanded ? 'Read less' : 'Read more'}
              <ChevronDown size={16} className={`transition-transform ${introExpanded ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </section>

      {/* About Section */}
      <AboutSection />

      {/* Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-2">
              What’s More?
            </p>

            <h2 className="font-display text-3xl font-semibold text-slate-900 max-w-3xl mx-auto leading-tight">
              We regularly provide our customers with exclusive discounts for group travel.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="text-center px-6 py-7 shadow-md rounded-[20px] border border-gray-100">
                <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon size={26} className="text-sky-500" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2 text-[20px]">{title}</h3>
                <p className="text-slate-500 text-md leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust banner */}
      <section className="py-14 px-6 bg-sky-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Safe and Reliable Travel
          </h2>
          <p className="text-sky-100 text-lg leading-relaxed">
            Book your flight with FlightAirHub with total confidence: we are a secure site chosen by thousands of passengers.
          </p>
        </div>
      </section>

      {/* Blog & Articles Preview */}
      <BlogArticlesPreview latestBlog={latestBlog} latestArticle={latestArticle} />

      {/* Global FAQs — shown just above the footer */}
      <div className='faqs-home'>
          <FaqAccordion faqs={faqs} />
      </div>

      {/* LARGE FOOTER */}
      <Footer />
    </div>
  );
}
